import { allScreenComponents, screenContainingComponent, selectedScreen } from './nativeScreens.js'
import { COMPONENT_REGISTRY, reconcileManifestCapabilities } from './nativeSportsbookManifest'
import { sectionDuplicationIssue } from './nativeDuplication'
import { builderIdentities } from './parlayBuilderModel'

const identities = (component) => [component, ...(component.cards ?? []), ...(component.columns ?? []), ...builderIdentities(component), ...(component.collection ? [component.collection] : [])]
const fixedFields = new Set(['id', 'slot_id', 'type'])
const equal = (left, right) => JSON.stringify(canonical(left)) === JSON.stringify(canonical(right))
function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical)
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]))
  return value
}

export function componentOverrides(base, component) {
  return Object.fromEntries([...new Set([...Object.keys(base), ...Object.keys(component)])]
    .filter((key) => !fixedFields.has(key) && !equal(base[key], component[key]))
    .map((key) => [key, component[key] ?? null]))
}

export function effectiveSectionBindings(draft, bindings = []) {
  const components = allScreenComponents(draft)
  return bindings.flatMap((binding) => {
    const component = components.find((item) => item.id === binding.component_id)
    return component ? [{ ...binding, overrides: componentOverrides(binding.base, component) }] : []
  })
}

export function mappedSection(section, previous, uuid = () => crypto.randomUUID()) {
  const base = structuredClone(section.component)
  const identityMap = {}
  for (const item of identities(base)) {
    for (const key of ['id', 'slot_id']) {
      if (!item[key]) continue
      identityMap[item[key]] = previous?.identity_map?.[item[key]] ?? uuid()
      item[key] = identityMap[item[key]]
    }
  }
  if (base.rank_column_id) base.rank_column_id = identityMap[base.rank_column_id] ?? base.rank_column_id
  return { component_id: base.id, section_id: section.section_id, version: section.version, name: section.name, identity_map: identityMap, base, overrides: {} }
}

export function bindExistingSection(draft, bindings, componentId, section) {
  const component = allScreenComponents(draft).find((item) => item.id === componentId)
  if (!component) throw new Error('The section was removed. Insert the saved section from the library.')
  const identityMap = {}
  for (const item of identities(component)) for (const key of ['id', 'slot_id']) if (item[key]) identityMap[item[key]] = item[key]
  const binding = mappedSection(section, { identity_map: identityMap })
  if (binding.component_id !== componentId) throw new Error('This saved section belongs to a different instance.')
  return [...bindings.filter((item) => item.component_id !== componentId), binding]
}

export function insertReusableSection(draft, bindings, section, afterId, registry = COMPONENT_REGISTRY, uuid, screenId) {
  if (section.archived) throw new Error('This section is archived. Restore it in the library before inserting it.')
  const issue = sectionDuplicationIssue(draft, section.component, registry, screenId)
  if (issue) throw new Error(issue)
  const binding = mappedSection(section, null, uuid)
  const next = structuredClone(draft)
  const destScreen = selectedScreen(next, { screenId })
  // The binding base stays the library's component; the hub scope is a recorded
  // instance override so "Update instances" and resets keep the destination competition.
  const instance = scopedToScreen(structuredClone(binding.base), destScreen)
  if (instance.competition !== binding.base.competition) binding.overrides = { ...binding.overrides, competition: structuredClone(instance.competition) }
  const list = destScreen.components
  const selectedIndex = list.findIndex((item) => item.id === afterId)
  const protectedIndex = list.findIndex((item) => item.type === 'compliance_notice')
  const limit = protectedIndex === -1 ? list.length : protectedIndex
  list.splice(selectedIndex === -1 ? limit : Math.min(selectedIndex + 1, limit), 0, instance)
  for (const scene of destScreen.context ? [] : next.experience?.home_scenes ?? []) {
    const index = scene.component_order.indexOf(afterId)
    const requiredIndex = scene.component_order.findIndex((id) => list.find((item) => item.id === id)?.type === 'compliance_notice')
    const sceneLimit = requiredIndex === -1 ? scene.component_order.length : requiredIndex
    scene.component_order.splice(index === -1 ? sceneLimit : Math.min(index + 1, sceneLimit), 0, binding.component_id)
  }
  return { draft: reconcileManifestCapabilities(next, registry), bindings: [...bindings, binding], componentId: binding.component_id }
}

function withOverrides(base, overrides) {
  const component = structuredClone(base)
  for (const [key, value] of Object.entries(overrides)) {
    if (fixedFields.has(key)) continue
    if (value === null) delete component[key]
    else component[key] = structuredClone(value)
  }
  return component
}

export function updateReusableInstances(draft, bindings, section, registry = COMPONENT_REGISTRY, uuid) {
  const next = structuredClone(draft)
  const updated = effectiveSectionBindings(draft, bindings).map((binding) => {
    if (binding.section_id !== section.section_id) return binding
    const components = screenContainingComponent(next, binding.component_id).components
    const index = components.findIndex((item) => item.id === binding.component_id)
    // A card newly promoted from this instance becomes a definition identity.
    // Keep its existing instance UUID; other instances receive fresh UUIDs.
    const currentIds = new Set(identities(components[index]).flatMap((item) => [item.id, item.slot_id]).filter(Boolean))
    const identityMap = { ...binding.identity_map }
    for (const item of identities(section.component)) for (const key of ['id', 'slot_id']) {
      if (item[key] && !identityMap[item[key]] && currentIds.has(item[key])) identityMap[item[key]] = item[key]
    }
    const replacement = mappedSection(section, { ...binding, identity_map: identityMap }, uuid)
    const screen = screenContainingComponent(next, binding.component_id)
    components[index] = scopedToScreen(withOverrides(replacement.base, binding.overrides), screen)
    return { ...replacement, overrides: binding.overrides }
  })
  return { draft: reconcileManifestCapabilities(next, registry), bindings: updated }
}

// A board ranks by one of its own columns, so resetting either restores both;
// otherwise the rank could name a column the reset removed.
const linkedFields = { columns: ['columns', 'rank_column_id'], rank_column_id: ['columns', 'rank_column_id'] }

export function resetSectionOverride(draft, bindings, componentId, field) {
  const binding = bindings.find((item) => item.component_id === componentId)
  if (!binding || fixedFields.has(field)) return draft
  const next = structuredClone(draft)
  const component = allScreenComponents(next).find((item) => item.id === componentId)
  if (field === undefined) Object.assign(component, withOverrides(binding.base, {}))
  else for (const key of component.type === 'player_prop_board' ? linkedFields[field] ?? [field] : [field]) {
    if (binding.base[key] === undefined) delete component[key]
    else component[key] = structuredClone(binding.base[key])
  }
  if (field === undefined) for (const key of Object.keys(component)) if (!(key in binding.base)) delete component[key]
  scopedToScreen(component, screenContainingComponent(next, componentId))
  return next
}

// A competition markets instance always belongs to the hub that hosts it; the
// library base cannot carry another hub's league into this screen.
function scopedToScreen(component, screen) {
  if (component.type === 'competition_markets' && screen?.context?.kind === 'competition') {
    component.competition = { sport: screen.context.sport, name: screen.context.competition }
  }
  return component
}

// Shared edits retain definition identities. Only known authored identities are
// translated; provider references, asset URLs and navigation routes are untouched.
export function definitionFromInstance(component, binding) {
  const result = structuredClone(component)
  const reverse = Object.fromEntries(Object.entries(binding.identity_map).map(([source, target]) => [target, source]))
  for (const item of identities(result)) for (const key of ['id', 'slot_id']) if (item[key] && reverse[item[key]]) item[key] = reverse[item[key]]
  if (result.rank_column_id && reverse[result.rank_column_id]) result.rank_column_id = reverse[result.rank_column_id]
  return result
}

export function sectionFieldName(field, component, registry = COMPONENT_REGISTRY) {
  const definition = registry.components.find((item) => item.type === component.type)
  const names = { cards: 'Cards', visibility: 'Audience', enabled: 'Visibility', title: 'Title', event_ids: 'Games', collection: 'Collection', style: 'Appearance' }
  return names[field] ?? definition?.editor.fields.find((item) => item.path === field)?.label ?? field.replaceAll('_', ' ')
}

export function sectionLayoutNames(draft, componentId) {
  const screen = screenContainingComponent(draft, componentId)
  if (screen?.context) return [screen.title]
  return ['Default Home', ...(draft.experience?.home_scenes ?? []).filter((scene) => !scene.hidden_component_ids.includes(componentId)).map((scene) => scene.name)]
}

export function compareSectionBindings(before = [], after = []) {
  const old = new Map(before.map((binding) => [binding.component_id, binding]))
  const next = new Map(after.map((binding) => [binding.component_id, binding]))
  const label = (binding) => binding ? `${binding.name} · Version ${binding.version}` : 'Independent content'
  return [...new Set([...old.keys(), ...next.keys()])].flatMap((id) => {
    const left = old.get(id), right = next.get(id)
    if (left?.section_id === right?.section_id && left?.version === right?.version) return []
    return [{ key: `reuse:${id}`, title: `Reusable section · ${(right || left).name}`, rows: [{ label: 'Linked version', before: label(left), after: label(right) }] }]
  })
}
